import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { EmptyState } from './EmptyState';
import { Loading } from './Loading';
import { ErrorAlert } from './ErrorAlert';
import { apiFetch } from '@/lib/api';
import { pnlColorClass } from '@/lib/colors';

interface Position {
  ticker: string;
  name?: string;
  quantity: number;
  avgPrice: number;
  currentPrice: number;
  value: number;
  floatingPnl: number;
  tCount?: number;
  tWinRate?: number;
  tProfit?: number;
}

interface AccountData {
  totalAssets: number;
  cash: number;
  marketValue: number;
  floatingPnl: number;
  realizedPnl: number;
  positions: Position[];
}

const formatMoney = (v: number) =>
  v.toLocaleString(undefined, { minimumFractionDigits: 2 });

const fmtPnl = (v: number) =>
  `${v >= 0 ? '+' : ''}${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AccountData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = () => {
      apiFetch('/api/account')
        .then((r) => r.json())
        .then((d) => {
          setData(d);
          setLoading(false);
        })
        .catch(() => {
          setError('无法加载数据，请确保后端服务已启动');
          setLoading(false);
        });
    };
    load();
    const timer = setInterval(load, 10000); // auto-refresh every 10s
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Loading />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <ErrorAlert>{error}</ErrorAlert>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.positions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>资产总览</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="暂无持仓数据"
            description="请先在「做T助手」页面添加股票或录入交易"
          />
        </CardContent>
      </Card>
    );
  }

  const statCards = [
    { label: '总资产', value: data.totalAssets, showSign: false, color: '' },
    { label: '可用现金', value: data.cash, showSign: false, color: '' },
    { label: '持仓市值', value: data.marketValue, showSign: false, color: '' },
    { label: '浮动盈亏', value: data.floatingPnl, showSign: true, color: pnlColorClass(data.floatingPnl >= 0) },
    { label: '已实现盈亏', value: data.realizedPnl, showSign: true, color: pnlColorClass(data.realizedPnl >= 0) },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>资产总览</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {statCards.map((s) => (
            <div
              key={s.label}
              className="rounded-[6px] border border-[#e5edf5] p-4 bg-[#fafbfd]"
            >
              <p className="text-xs text-[#64748d] mb-1">{s.label}</p>
              <p className={`text-xl font-[300] tracking-[-0.02em] tabular-nums ${s.color || 'text-[#061b31]'}`}>
                {s.showSign ? fmtPnl(s.value) : formatMoney(s.value)}
              </p>
            </div>
          ))}
        </div>

        {/* Positions table (desktop) */}
        <h3 className="text-sm font-[500] text-[#061b31] mb-3 hidden md:block">持仓明细</h3>
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>代码</TableHead>
                <TableHead className="text-right">数量</TableHead>
                <TableHead className="text-right">成本价</TableHead>
                <TableHead className="text-right">现价</TableHead>
                <TableHead className="text-right">市值</TableHead>
                <TableHead className="text-right">浮动盈亏</TableHead>
                <TableHead className="text-right">做T次数</TableHead>
                <TableHead className="text-right">做T胜率</TableHead>
                <TableHead className="text-right">做T盈利</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.positions.map((asset) => (
                <TableRow key={asset.ticker}>
                  <TableCell className="font-mono font-medium">{asset.ticker}</TableCell>
                  <TableCell className="text-right tabular-nums">{asset.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums">{asset.avgPrice.toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums">{asset.currentPrice.toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(asset.value)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Badge variant={asset.floatingPnl >= 0 ? 'default' : 'destructive'}>
                      {fmtPnl(asset.floatingPnl)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{asset.tCount ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className={`${(asset.tWinRate ?? 0) >= 50 ? 'text-[#15be53]' : 'text-[#ea2261]'}`}>
                      {(asset.tWinRate ?? 0).toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className={pnlColorClass((asset.tProfit ?? 0) >= 0)}>
                      {(asset.tProfit ?? 0) >= 0 ? '+' : ''}{(asset.tProfit ?? 0).toFixed(2)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Positions cards (mobile) */}
        <div className="grid grid-cols-1 gap-3 md:hidden">
          {data.positions.map((asset) => {
            const profitPct = asset.avgPrice > 0 ? ((asset.currentPrice - asset.avgPrice) / asset.avgPrice * 100) : 0;
            const isUp = asset.floatingPnl >= 0;
            return (
              <div key={asset.ticker} className="rounded-[12px] border border-[#e5edf5] bg-white p-4">
                {/* Header: ticker + name */}
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-mono font-bold text-[#061b31] text-lg">{asset.ticker}</span>
                    {asset.name && asset.name !== asset.ticker && (
                      <span className="ml-2 text-sm text-[#64748d]">{asset.name}</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium tabular-nums ${pnlColorClass(isUp)}`}>
                    {isUp ? '+' : ''}{profitPct.toFixed(2)}%
                  </span>
                </div>

                {/* Big price */}
                <div className={`text-[32px] font-[300] tracking-[-0.03em] tabular-nums mb-3 ${pnlColorClass(isUp)}`}>
                  ¥{asset.currentPrice.toFixed(2)}
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">持仓</span>
                    <span className="tabular-nums text-[#061b31]">{asset.quantity} 股</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">成本</span>
                    <span className="tabular-nums text-[#061b31]">¥{asset.avgPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">市值</span>
                    <span className="tabular-nums text-[#061b31]">¥{formatMoney(asset.value)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">浮动盈亏</span>
                    <span className={`tabular-nums font-medium ${pnlColorClass(isUp)}`}>
                      {fmtPnl(asset.floatingPnl)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">做T次数</span>
                    <span className="tabular-nums text-[#061b31]">{asset.tCount ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748d]">做T胜率</span>
                    <span className={`tabular-nums ${(asset.tWinRate ?? 0) >= 50 ? 'text-[#15be53]' : 'text-[#ea2261]'}`}>
                      {(asset.tWinRate ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between col-span-2">
                    <span className="text-[#64748d]">做T盈利</span>
                    <span className={`tabular-nums font-medium ${pnlColorClass((asset.tProfit ?? 0) >= 0)}`}>
                      {(asset.tProfit ?? 0) >= 0 ? '+' : ''}¥{(asset.tProfit ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default Dashboard;
